"use client";

import { useEffect, useRef } from "react";

/**
 * Landing ViewContent — dual setup. Fires the browser Pixel + server-side
 * Conversions API `ViewContent` (shared `eventId`, Meta deduplicates) ONCE
 * when the pricing section scrolls into view — the funnel step between
 * PageView and InitiateCheckout. Anonymous, no PII (same as the landing
 * PageView). Mirrors `FacebookPixelPageView`: if `window.fbq` is absent the
 * Pixel didn't render (opted out / no id), so we skip BOTH halves.
 *
 * Renders a zero-height sentinel; place it at the top of the pricing section.
 */
export function FacebookPixelViewContent({
  contentName = "pricing",
}: {
  contentName?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
    if (!fbq) return; // Pixel not active (opted out / not configured).

    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;

      const eventId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `vc-${Date.now()}-${Math.round(Math.random() * 1e9)}`;

      fbq("track", "ViewContent", { content_name: contentName }, { eventID: eventId });

      try {
        void fetch("/api/conversions/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body: JSON.stringify({
            eventName: "ViewContent",
            eventId,
            eventSourceUrl: window.location.href,
          }),
        });
      } catch {
        /* ignore */
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            fire();
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [contentName]);

  return <div ref={ref} aria-hidden style={{ height: 0 }} />;
}

export default FacebookPixelViewContent;
